import React, { useEffect, useState } from "react";
import { Flex, Image } from "rebass/styled-components";
import styled from "styled-components";

import { Body, Title2 } from "../Text";
import { Button } from "../Button";
import { useUser } from "../../Hooks/useUser";
import { useHistory } from "react-router-dom";
import { ClaimMontageViewModel } from "./ViewModel";

interface Friend {
  username: string;
  avatar: string;
}

interface IClaimMontageBannerProps {
  data: {
    title: string;
    text: string;
    button: string;
    onClick: () => void;
  };
  claimed?: boolean;
  friends?: Friend[];
  loading: boolean;
}

const currentUrl = window.location.href.replace(window.location.origin, "");

const ClaimMontage = ({ clipId }: { clipId: string }) => {
  const { getClaimSquadMontage, postClaimSquadMontage } =
    ClaimMontageViewModel();
  const [claimable, setClaimable] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [friends, setFriends] = useState();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const { allstarUser } = useUser();
  const history = useHistory();
  const checkClaimable = async () => {
    if (!clipId) return;

    const data = await getClaimSquadMontage(clipId);

    if (data.claimable) {
      setFriends(data.featured.slice(0, 5));
      setClaimable(true);
    }
  };
  const claim = async () => {
    try {
      setLoading(true);
      const response = await postClaimSquadMontage(clipId);
      setLoading(false);
      setClaimed(response);
    } catch (e) {
      setError(true);
    }
  };
  const claimMontageData = {
    visitor: {
      title: "See yourself in this Montage?",
      text: "Log in to claim this clip and add it to your Allstar profile! We’ll add any effects you have applied to your loadout.",
      button: "Login to Claim",
      onClick: () => history.push(`/login?next=${currentUrl}`),
    },
    claimable: {
      title: "You and your friends are featured in this Montage!",
      text: "Dang you're looking good, should hang onto this one... Claim this clip and we’ll add it to your profile, along with any effects you have applied to your loadout!",
      button: "Claim Squad Montage",
      onClick: claim,
    },
    claimed: {
      title: "You and your friends are featured in this Montage!",
      text: "We're adding this clip to your profile, check back soon!",
      button: "Claim Squad Montage",
      onClick: () => {},
    },
    error: {
      title: "You and your friends are featured in this Montage!",
      text: "Something went wrong with claiming this montage...",
      button: "Claim Squad Montage",
      onClick: () => {},
    },
  };

  useEffect(() => {
    if (!allstarUser.loggedIn) return;
    checkClaimable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allstarUser.loggedIn]);

  if (error)
    return (
      <ClaimMontageBanner
        data={claimMontageData.error}
        friends={friends}
        claimed={true}
        loading={loading}
      />
    );

  if (!allstarUser.loggedIn)
    return (
      <ClaimMontageBanner data={claimMontageData.visitor} loading={loading} />
    );

  if (claimable && claimed)
    return (
      <ClaimMontageBanner
        data={claimMontageData.claimed}
        claimed={true}
        friends={friends}
        loading={loading}
      />
    );

  if (claimable)
    return (
      <ClaimMontageBanner
        data={claimMontageData.claimable}
        friends={friends}
        loading={loading}
      />
    );
  return <></>;
};

const ClaimMontageBanner = ({
  data,
  claimed,
  friends,
  loading,
}: IClaimMontageBannerProps) => (
  <StyledContainer flexDirection="column">
    <Flex
      style={{ gap: "20px" }}
      flexWrap="wrap"
      justifyContent="center"
      mb={3}
    >
      {friends &&
        friends.map((friend) => (
          <Friend username={friend.username} avatar={friend.avatar} />
        ))}
    </Flex>
    <Title2 mt={3} fontWeight="title3">
      {data.title}
    </Title2>
    <Body color="chalk" fontWeight="title3" mt={6}>
      {data.text}
    </Body>
    <Button
      size="tiny"
      flex={true}
      px={6}
      mt={6}
      mb={3}
      onClick={data.onClick}
      disabled={claimed || loading}
    >
      {loading ? "Working..." : data.button}
    </Button>
  </StyledContainer>
);

const Friend = ({ username, avatar }: { username: string; avatar: string }) => {
  return (
    <StyledFriend>
      <StyledImage src={avatar} />
      <Body fontSize={1} color="envy" fontWeight="subheader">
        {username}
      </Body>
    </StyledFriend>
  );
};

const StyledImage = styled(Image)`
  height: 32px;
  width: 32px;
  border-radius: 24px;
  position: absolute;
  left: -12px;
  top: -2px;
  border: 1px solid ${(props) => props.theme.colors.midnight};
`;

const StyledFriend = styled(Flex)`
  border-radius: 20px;
  background-color: ${(props) => props.theme.colors.darkerNed};
  border: 1px solid ${(props) => props.theme.colors.midnight};
  padding: 4px 20px 4px 30px;
  position: relative;
`;

const StyledContainer = styled(Flex)`
  background-color: ${(props) => props.theme.colors.nedLight};
  box-shadow: 0px 2px 15px ${(props) => props.theme.colors.midnightAlpha50};
  border-radius: 10px;
  width: 80%;
  max-width: 600px;
  padding: 30px 40px;
  margin: 40px 0 0 0;
  align-items: center;
  justify-content: center;
  text-align: center;
`;
export { ClaimMontage };
